import { useState } from "react";
import { useTrail, animated } from "@react-spring/web";
import { Plus } from "lucide-react";
import { useGitPullRequests } from "@/hooks/git";
import type { GitPullRequest as GitPullRequestType } from "@/services/git/types";
import { ListSkeleton, EmptyList } from "./shared";

// ─── PR List ──────────────────────────────────────────────────────────────────

export function PrList({
  repoFullName, selectedNumber, onSelect, onCreate,
}: {
  repoFullName: string;
  selectedNumber: number | null;
  onSelect: (number: number) => void;
  onCreate: () => void;
}) {
  const [prState, setPrState] = useState<"open" | "closed">("open");
  const { data: prs = [], isLoading } = useGitPullRequests(repoFullName, prState);

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-border/50">
        {(["open", "closed"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setPrState(s)}
            className={[
              "flex flex-1 items-center justify-center py-2 text-xs font-medium capitalize transition-colors",
              prState === s ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="border-b border-border/50 px-3 py-2">
        <button
          type="button"
          onClick={onCreate}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Pull Request
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <ListSkeleton />
        ) : prs.length === 0 ? (
          <EmptyList text={`No ${prState} pull requests.`} />
        ) : (
          <PrItems key={prState} prs={prs} selectedNumber={selectedNumber} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}

function PrItems({
  prs, selectedNumber, onSelect,
}: {
  prs: GitPullRequestType[];
  selectedNumber: number | null;
  onSelect: (number: number) => void;
}) {
  const trail = useTrail(prs.length, {
    from: { opacity: 0, y: 4 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 34, clamp: true },
  });

  return (
    <>
      {trail.map((style, i) => {
        const pr = prs[i];
        return (
          <animated.button
            key={pr.number}
            style={style}
            type="button"
            onClick={() => onSelect(pr.number)}
            className={[
              "w-full border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-0",
              pr.number === selectedNumber ? "bg-primary/10" : "hover:bg-muted/30",
            ].join(" ")}
          >
            <p className="truncate text-xs font-medium leading-snug">{pr.title}</p>
            <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
              <span className="text-primary/80">#{pr.number}</span>
              <span>·</span>
              <span>{pr.author_login}</span>
              {pr.created_at && (
                <>
                  <span>·</span>
                  <span>{new Date(pr.created_at).toLocaleDateString()}</span>
                </>
              )}
            </p>
          </animated.button>
        );
      })}
    </>
  );
}
